import Dialog from "@mui/material/Dialog";
import type { PaperProps } from "@mui/material/Paper";
import React, { useCallback, useEffect, useState } from "react";
import * as Styled from "./FloatingDevtoolsDialog.styled";

type FloatingDevtoolsDialogProps = {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
};

type PaperComponentProps = PaperProps & { fullscreen: boolean; position?: { x: number; y: number } };
const PaperComponentWithFullscreen: React.FC<PaperComponentProps> = (props) => {
    const { fullscreen, position, ...rest } = props;
    const Container = fullscreen ? Styled.FullscreenDialogContainer : Styled.FixedDialogContainer;
    return (
        <Container style={position && !fullscreen ? { left: position.x, top: position.y, bottom: "auto" } : undefined}>
            <Styled.StyledPaper {...rest} />
        </Container>
    );
};

function useDraggable(fullscreen: boolean): {
    position: { x: number; y: number };
    handleMouseDown: (e: React.MouseEvent) => void;
} {
    const [position, setPosition] = useState({ x: 0, y: window.innerHeight - Math.round(window.innerHeight * 0.4) });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent): void => {
        if (fullscreen) return;
        const target = e.target as HTMLElement;
        // Only drag from the header, not from buttons
        if (target.tagName === "BUTTON" || target.closest("button")) return;

        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent): void => {
            if (!isDragging) return;

            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            setPosition({ x: newX, y: newY });
        },
        [isDragging, dragOffset],
    );

    const handleMouseUp = useCallback((): void => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return { position, handleMouseDown };
}

export const FloatingDevtoolsDialog: React.FC<FloatingDevtoolsDialogProps> = ({ open, onClose, children }) => {
    const [fullscreen, setFullscreen] = useState(false);
    const { position, handleMouseDown } = useDraggable(fullscreen);

    // Prevent closing on outside click
    const handleClose = (_event: object, reason?: string): void => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            // ignore backdrop and escape
            return;
        }
        onClose();
    };
    const handleFullscreen = (): void => {
        setFullscreen((f) => !f);
    };

    // Stable PaperComponent for Dialog
    const DialogPaperComponent = useCallback(
        function DialogPaperComponent(props: PaperProps) {
            return <PaperComponentWithFullscreen {...props} fullscreen={fullscreen} position={position} />;
        },
        [fullscreen, position],
    );
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            hideBackdrop
            disableEnforceFocus
            disableScrollLock
            PaperComponent={DialogPaperComponent}
            aria-labelledby="floating-dialog-drag-handle"
            style={{ zIndex: 13000, pointerEvents: "none" }}
            PaperProps={{ style: { pointerEvents: "auto" } }}
        >
            <Styled.DragHandle
                id="floating-dialog-drag-handle"
                onMouseDown={handleMouseDown}
                style={{ cursor: fullscreen ? "default" : "move" }}
            >
                React Query Devtools hacking console
                <div>
                    <Styled.FullscreenButton
                        type="button"
                        onClick={handleFullscreen}
                        aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                    >
                        {fullscreen ? "⤡" : "⤢"}
                    </Styled.FullscreenButton>
                    <Styled.CloseButton onClick={onClose} aria-label="Close">
                        x
                    </Styled.CloseButton>
                </div>
            </Styled.DragHandle>
            <Styled.DevtoolsContent>{children}</Styled.DevtoolsContent>
        </Dialog>
    );
};
